/**
 * DustSwapFilterToggle Component
 *
 * Toggle switch to filter token list to show only tokens eligible for dust swapping.
 * Displays count of swappable tokens and description text.
 */

import { Filter } from 'lucide-react';

interface DustSwapFilterToggleProps {
  /** Whether the filter is currently active */
  active: boolean;
  /** Callback when toggle state changes */
  onToggle: (active: boolean) => void;
  /** Number of tokens eligible for dust swapping */
  swappableCount: number;
  /** Total number of tokens */
  totalCount: number;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Whether prices are currently loading */
  isLoading?: boolean;
}

export function DustSwapFilterToggle({
  active,
  onToggle,
  swappableCount,
  totalCount,
  disabled = false,
  isLoading = false,
}: DustSwapFilterToggleProps) {
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border p-3 transition-colors sm:flex-row sm:items-center sm:justify-between"
      style={{
        backgroundColor: active ? 'rgba(80, 228, 249, 0.1)' : 'rgba(255, 255, 255, 0.05)',
        borderColor: active ? '#50E5F8' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <div className="flex items-center gap-2">
        <Filter
          className="h-4 w-4 flex-shrink-0"
          style={{ color: active ? '#50E5F8' : '#9CA3AF' }}
        />
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-medium"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: active ? '#50E5F8' : '#FFFFFF',
            }}
          >
            Show only swappable tokens
          </div>
          <div
            className="text-xs"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: 'rgba(255, 255, 255, 0.6)',
            }}
          >
            Filter tokens with recoverable value (&gt;0.002 SOL)
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Count Badge */}
        <div
          className="flex-shrink-0 rounded px-2 py-1 text-xs"
          style={{
            fontFamily: 'Outfit, sans-serif',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            color: swappableCount > 0 ? '#4ADE80' : '#E5E7EB',
          }}
        >
          {isLoading ? (
            <span className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Checking...
            </span>
          ) : (
            `${swappableCount} of ${totalCount} swappable`
          )}
        </div>

        {/* Toggle Switch */}
        <button
          onClick={() => onToggle(!active)}
          disabled={disabled || isLoading}
          className="relative h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            backgroundColor: active ? '#50E5F8' : 'rgba(255, 255, 255, 0.2)',
            focusRing: '#50E5F8',
          }}
          aria-pressed={active}
          aria-label="Toggle swappable tokens filter"
        >
          <span
            className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
            style={{
              transform: active ? 'translateX(20px)' : 'translateX(0)',
            }}
          />
        </button>
      </div>
    </div>
  );
}
